import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, Gavel, Trash2, Pencil, Calculator, CheckCircle2, AlertTriangle, Check, Clock, CheckCheck, Radio, MonitorPlay, History, FileDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSiteLogo } from "@/lib/siteConfig";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { APPARATUS, EXERCISE_TYPES } from "@/components/AthleteFormDialog";

const discLabel = { artistica: "Artistica", ritmica: "Ritmica" };
const MANAGER_ROLES = ["admin", "segreteria", "direttore"];
const emptyScore = { judge_id: "", apparatus: "", exercise_type: "", difficulty: "", execution: "", artistry: "", penalty: "", notes: "" };
const POLL_MS = 4000;

const AUDIT_ACTIONS = {
  create: { label: "Inserito", c: "text-[#38BDF8] border-[#38BDF8]/40 bg-[#38BDF8]/10" },
  update: { label: "Modificato", c: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  confirm: { label: "Confermato", c: "text-[#10B981] border-[#10B981]/40 bg-[#10B981]/10" },
  unconfirm: { label: "Conferma annullata", c: "text-orange-300 border-orange-500/40 bg-orange-500/10" },
  bulk_confirm: { label: "Conferma multipla", c: "text-[#10B981] border-[#10B981]/40 bg-[#10B981]/10" },
  delete: { label: "Eliminato", c: "text-red-300 border-red-500/40 bg-red-500/10" },
};
const fmtAuditDate = (iso) => {
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};
const fmtAuditDateFull = (iso) => {
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};
const auditTotalsText = (a) => {
  if (a.action === "create" && a.new_total != null) return `Tot. ${Number(a.new_total).toFixed(3)}`;
  if (a.action === "update") return `${a.old_total != null ? Number(a.old_total).toFixed(3) : "—"} → ${a.new_total != null ? Number(a.new_total).toFixed(3) : "—"}`;
  if (a.action === "delete" && a.old_total != null) return `era Tot. ${Number(a.old_total).toFixed(3)}`;
  if (a.action === "bulk_confirm" && a.count) return `${a.count} voti`;
  return "";
};
async function urlToImage(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader(); r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(blob);
  });
  const img = await new Promise((resolve, reject) => {
    const im = new window.Image(); im.onload = () => resolve(im); im.onerror = reject; im.src = dataUrl;
  });
  const fmt = dataUrl.substring(dataUrl.indexOf("/") + 1, dataUrl.indexOf(";")).toUpperCase();
  return { dataUrl, width: img.naturalWidth, height: img.naturalHeight, fmt: fmt === "JPG" ? "JPEG" : fmt };
}

export default function JudgesSection() {
  const { user } = useAuth();
  const logoUrl = useSiteLogo();
  const isManager = MANAGER_ROLES.includes(user?.role);
  const isJudge = user?.role === "judge";
  const canAudit = user?.role === "admin" || user?.role === "direttore";

  const [athletes, setAthletes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [athleteId, setAthleteId] = useState("");
  const [scores, setScores] = useState([]);
  const [judges, setJudges] = useState([]);
  const [activeJudges, setActiveJudges] = useState(0);
  const [pending, setPending] = useState({ count: 0, items: [] });
  const [form, setForm] = useState(emptyScore);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pedana, setPedana] = useState(null);

  const loadPending = useCallback(() => {
    api.get("/scores/pending").then((r) => setPending(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/athletes").then((r) => setAthletes(r.data)),
      api.get("/groups").then((r) => setGroups(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
    loadPending();
  }, [loadPending]);

  // Adatta il n. di giudici attesi e l'elenco giudici alla gara dell'atleta selezionato
  useEffect(() => {
    const params = athleteId ? { athlete_id: athleteId } : {};
    api.get("/scores/config", { params }).then((r) => setActiveJudges(r.data.active_judges)).catch(() => {});
    if (isManager) api.get("/scores/judges", { params }).then((r) => setJudges(r.data)).catch(() => {});
  }, [athleteId, isManager]);

  // Aggancio pedana: segue l'atleta attualmente in pedana (LiveBoard) in tempo reale
  useEffect(() => {
    const loadPedana = () => api.get("/live/pedana").then((r) => setPedana(r.data)).catch(() => {});
    loadPedana();
    const t = setInterval(loadPedana, POLL_MS);
    return () => clearInterval(t);
  }, []);

  const loadScores = useCallback((id) => {
    if (!id) { setScores([]); return; }
    api.get("/scores", { params: { athlete_id: id } }).then((r) => setScores(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadScores(athleteId); }, [athleteId, loadScores]);

  // Aggiornamento LIVE: aggiorna voti, non-confermati e n. giudici attivi automaticamente
  useEffect(() => {
    if (!athleteId) return;
    const t = setInterval(() => {
      loadScores(athleteId);
      loadPending();
      api.get("/scores/config", { params: { athlete_id: athleteId } }).then((r) => setActiveJudges(r.data.active_judges)).catch(() => {});
    }, POLL_MS);
    return () => clearInterval(t);
  }, [athleteId, loadScores, loadPending]);

  // Giudici: sincronizza l'atleta con quello in pedana (tutti votano lo stesso)
  useEffect(() => {
    if (!isJudge) return;
    const cur = pedana?.current?.id || "";
    setAthleteId((prev) => {
      if (cur === prev) return prev;
      setEditingId(null);
      if (prev && cur && pedana?.current?.name) toast.info(`Nuovo atleta in pedana: ${pedana.current.name}`);
      return cur;
    });
  }, [pedana, isJudge]);

  // Giudici: precompila il form quando i dati dell'atleta sono disponibili (evita race condition)
  useEffect(() => {
    if (!isJudge || !athleteId || editingId) return;
    const grp = groups.find((x) => x.id === athleteId);
    const a = athletes.find((x) => x.id === athleteId);
    if (!grp && !a) return; // dati non ancora caricati
    setForm(() => ({ ...emptyScore, exercise_type: grp ? "" : (a?.exercise_type || a?.exercise_types?.[0] || ""), apparatus: grp ? "" : (a?.apparatus?.[0] || "") }));
  }, [athleteId, athletes, groups, isJudge, editingId]);

  const selectAthlete = (id) => {
    setAthleteId(id);
    setEditingId(null);
    const grp = groups.find((x) => x.id === id);
    const a = athletes.find((x) => x.id === id);
    setForm((f) => ({ ...emptyScore, judge_id: isJudge ? "" : f.judge_id, exercise_type: grp ? "" : (a?.exercise_type || a?.exercise_types?.[0] || ""), apparatus: grp ? "" : (a?.apparatus?.[0] || "") }));
  };

  const liveTotal = useMemo(() => {
    const n = (v) => parseFloat(v) || 0;
    return (n(form.difficulty) + n(form.execution) + n(form.artistry) - n(form.penalty)).toFixed(3);
  }, [form]);

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({ judge_id: s.judge_id, apparatus: s.apparatus || "", exercise_type: s.exercise_type || "", difficulty: s.difficulty, execution: s.execution, artistry: s.artistry, penalty: s.penalty, notes: s.notes || "" });
  };
  const cancelEdit = () => { setEditingId(null); setForm((f) => ({ ...emptyScore, judge_id: isJudge ? "" : f.judge_id })); };

  const canManage = (s) => isManager || (isJudge && s.judge_id === user.id);

  const submit = async () => {
    if (!athleteId) { toast.error("Seleziona un atleta"); return; }
    if (isManager && !editingId && !form.judge_id) { toast.error("Seleziona il giudice a cui attribuire il voto"); return; }
    setSaving(true);
    const payload = {
      athlete_id: athleteId, apparatus: form.apparatus, exercise_type: form.exercise_type,
      difficulty: parseFloat(form.difficulty) || 0, execution: parseFloat(form.execution) || 0,
      artistry: parseFloat(form.artistry) || 0, penalty: parseFloat(form.penalty) || 0,
      notes: form.notes,
    };
    if (isManager) payload.judge_id = form.judge_id;
    try {
      if (editingId) {
        await api.put(`/scores/${editingId}`, payload);
        toast.success("Voto modificato" + (isJudge ? " — ricordati di riconfermarlo" : ""));
      } else {
        await api.post("/scores", payload);
        toast.success("Voto salvato come bozza. Conferma per registrarlo.");
      }
      cancelEdit();
      loadScores(athleteId);
      loadPending();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const confirmScore = async (s) => {
    try {
      await api.put(`/scores/${s.id}/confirm`, { confirmed: true });
      toast.success("Voto confermato e registrato");
      loadScores(athleteId); loadPending();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  // Blocco anti-errore: avvisa se il totale è anomalo rispetto agli altri giudici
  const [anomaly, setAnomaly] = useState(null);
  const checkAndConfirm = (s) => {
    const others = scores.filter((x) => x.id !== s.id && x.confirmed && (x.apparatus || "") === (s.apparatus || ""));
    const total = Number(s.total) || 0;
    let reason = null;
    if (total <= 0) {
      reason = "Il totale è pari a zero.";
    } else if (others.length >= 1) {
      const avg = others.reduce((a, x) => a + (Number(x.total) || 0), 0) / others.length;
      const dev = Math.abs(total - avg);
      const rel = avg > 0 ? dev / avg : 0;
      if (dev >= 1.5 || rel >= 0.15) {
        reason = `Il tuo totale (${total.toFixed(3)}) si discosta molto dalla media degli altri giudici (${avg.toFixed(3)}).`;
      }
    }
    if (reason) setAnomaly({ score: s, reason });
    else confirmScore(s);
  };
  const unconfirmScore = async (s) => {
    try {
      await api.put(`/scores/${s.id}/confirm`, { confirmed: false });
      loadScores(athleteId); loadPending();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/scores/${id}`); toast.success("Voto eliminato"); loadScores(athleteId); loadPending(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const bulkConfirm = async (app) => {
    try {
      const { data } = await api.post("/scores/confirm-bulk", { athlete_id: athleteId, apparatus: app === "—" ? "" : app });
      toast.success(data.confirmed > 0 ? `${data.confirmed} voti confermati` : "Nessun voto da confermare");
      loadScores(athleteId); loadPending();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Registro voti (audit): solo admin/direttore
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState("all");
  const [auditFrom, setAuditFrom] = useState("");
  const [auditTo, setAuditTo] = useState("");
  const [auditEvent, setAuditEvent] = useState("all");
  const [auditEvents, setAuditEvents] = useState([]);
  const openAudit = async () => {
    setAuditOpen(true); setAuditLoading(true);
    if (auditEvents.length === 0) api.get("/events").then((r) => setAuditEvents(r.data)).catch(() => {});
    try { const { data } = await api.get("/scores/audit"); setAudit(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAuditLoading(false); }
  };
  const auditEventMap = useMemo(() => {
    const m = {};
    athletes.forEach((a) => { m[a.id] = a.event_id || ""; });
    groups.forEach((g) => { m[g.id] = g.event_id || ""; });
    return m;
  }, [athletes, groups]);
  const inAuditDateRange = (iso) => {
    if (!auditFrom && !auditTo) return true;
    const t = new Date(iso).getTime();
    if (auditFrom && t < new Date(`${auditFrom}T00:00:00`).getTime()) return false;
    if (auditTo && t > new Date(`${auditTo}T23:59:59`).getTime()) return false;
    return true;
  };
  const matchAuditEvent = (a) => auditEvent === "all" || auditEventMap[a.athlete_id] === auditEvent;
  const auditDateScoped = audit.filter((a) => inAuditDateRange(a.at) && matchAuditEvent(a));
  const auditFiltered = auditFilter === "all" ? auditDateScoped : auditDateScoped.filter((a) => a.action === auditFilter);

  const exportAuditCSV = () => {
    if (auditFiltered.length === 0) { toast.error("Nessuna voce da esportare"); return; }
    const header = ["Data", "Azione", "Atleta", "Attrezzo", "Giudice", "Autore", "Ruolo", "Dettaglio"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = auditFiltered.map((a) => [
      fmtAuditDateFull(a.at), (AUDIT_ACTIONS[a.action]?.label || a.action), a.athlete_name || "",
      a.apparatus || "", a.judge_name || "", a.actor_name || "", a.actor_role || "", auditTotalsText(a),
    ].map(esc).join(","));
    const csv = "\uFEFF" + [header.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = `registro-voti-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
    toast.success("CSV generato");
  };

  const exportAuditPDF = async () => {
    if (auditFiltered.length === 0) { toast.error("Nessuna voce da esportare"); return; }
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const now = new Date().toLocaleString("it-IT", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
    let textX = 14;
    if (logoUrl) {
      try {
        const logo = await urlToImage(logoUrl);
        const h = 16, w = Math.min(36, (logo.width / logo.height) * h);
        doc.addImage(logo.dataUrl, logo.fmt, 14, 10, w, h);
        textX = 14 + w + 5;
      } catch (e) { /* logo opzionale */ }
    }
    doc.setFontSize(16); doc.setTextColor(20, 20, 20); doc.text("OPES Ginnastiche", textX, 16);
    doc.setFontSize(11); doc.setTextColor(90, 90, 90); doc.text("Registro modifiche voti", textX, 23);
    doc.setFontSize(9); doc.setTextColor(120, 120, 120); doc.text(`Generato il ${now}`, textX, 28);
    doc.setDrawColor(212, 175, 55); doc.setLineWidth(0.5); doc.line(14, 32, 283, 32);
    autoTable(doc, {
      startY: 37,
      head: [["Data", "Azione", "Atleta", "Attrezzo", "Giudice", "Autore", "Ruolo", "Dettaglio"]],
      body: auditFiltered.map((a) => [
        fmtAuditDateFull(a.at), (AUDIT_ACTIONS[a.action]?.label || a.action), a.athlete_name || "—",
        a.apparatus || "—", a.judge_name || "—", a.actor_name || "—", a.actor_role || "—", auditTotalsText(a) || "—",
      ]),
      theme: "grid",
      headStyles: { fillColor: [212, 175, 55], textColor: [2, 6, 23], fontStyle: "bold", fontSize: 9 },
      styles: { fontSize: 8, cellPadding: 1.5 },
      margin: { left: 14, right: 14 },
    });
    doc.save(`registro-voti-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF generato");
  };

  const selectedGroup = groups.find((g) => g.id === athleteId);
  const selectedIsGroup = !!selectedGroup;
  const selected = athletes.find((a) => a.id === athleteId) || selectedGroup;
  const selectedName = selectedGroup ? selectedGroup.name : (selected ? `${selected.first_name} ${selected.last_name}` : "");

  // Completamento per attrezzo
  const byApparatus = useMemo(() => {
    const map = {};
    scores.forEach((s) => {
      const app = s.apparatus || "—";
      const g = map[app] || (map[app] = { confirmed: new Set(), pending: 0, total: 0 });
      if (s.confirmed) { g.confirmed.add(s.judge_id); g.total += s.total; }
      else g.pending += 1;
    });
    return map;
  }, [scores]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>;

  return (
    <div className="space-y-6">
      {isManager && (
        <div className="flex justify-end gap-2">
          {canAudit && (
            <button data-testid="open-audit-log" onClick={openAudit} className="inline-flex items-center gap-2 rounded-lg border border-white/15 text-muted-foreground hover:text-foreground hover:border-white/30 transition-colors px-4 py-2 text-sm font-medium">
              <History className="w-4 h-4" /> Registro voti
            </button>
          )}
          <Link to="/giuria-live" data-testid="open-director-live" className="inline-flex items-center gap-2 rounded-lg border border-gold/40 bg-gold/10 text-gold hover:bg-gold/20 transition-colors px-4 py-2 text-sm font-semibold">
            <MonitorPlay className="w-4 h-4" /> Vista Direttore (schermo intero)
          </Link>
        </div>
      )}

      {/* Registro voti (audit log) */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent data-testid="audit-dialog" className="bg-[#0F172A] border-white/10 max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="w-5 h-5 text-gold" /> Registro modifiche voti</DialogTitle>
            <DialogDescription>Storico di chi ha inserito, modificato, confermato o eliminato ogni voto e quando.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <div className="flex flex-wrap items-end gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-3">
              <div>
                <Label className="text-xs text-muted-foreground">Gara</Label>
                <Select value={auditEvent} onValueChange={setAuditEvent}>
                  <SelectTrigger data-testid="audit-event-select" className="mt-1 h-9 w-[220px]"><SelectValue placeholder="Tutte le gare" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutte le gare</SelectItem>
                    {auditEvents.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Dal</Label>
                <Input data-testid="audit-date-from" type="date" value={auditFrom} onChange={(e) => setAuditFrom(e.target.value)} className="mt-1 h-9 w-[160px]" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Al</Label>
                <Input data-testid="audit-date-to" type="date" value={auditTo} onChange={(e) => setAuditTo(e.target.value)} className="mt-1 h-9 w-[160px]" />
              </div>
              {(auditFrom || auditTo || auditEvent !== "all") && (
                <button data-testid="audit-date-clear" onClick={() => { setAuditFrom(""); setAuditTo(""); setAuditEvent("all"); }} className="h-9 text-xs text-muted-foreground hover:text-gold underline">Azzera filtri</button>
              )}
              <span className="text-xs text-muted-foreground ml-auto self-center">{auditFiltered.length} voci nel periodo</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <button data-testid="audit-filter-all" onClick={() => setAuditFilter("all")} className={`px-3 py-1 rounded-full border text-xs transition-colors ${auditFilter === "all" ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-muted-foreground hover:border-gold/40"}`}>Tutte ({auditDateScoped.length})</button>
              {Object.entries(AUDIT_ACTIONS).map(([k, v]) => {
                const n = auditDateScoped.filter((a) => a.action === k).length;
                if (!n) return null;
                return <button key={k} data-testid={`audit-filter-${k}`} onClick={() => setAuditFilter(k)} className={`px-3 py-1 rounded-full border text-xs transition-colors ${auditFilter === k ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-muted-foreground hover:border-gold/40"}`}>{v.label} ({n})</button>;
              })}
            </div>
          </div>
          <div className="overflow-y-auto flex-1 -mx-1 px-1">
            {auditLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
            ) : auditFiltered.length === 0 ? (
              <p className="text-muted-foreground text-sm py-12 text-center" data-testid="audit-empty">Nessuna modifica registrata.</p>
            ) : (
              <div className="space-y-2">
                {auditFiltered.map((a) => {
                  const act = AUDIT_ACTIONS[a.action] || { label: a.action, c: "text-muted-foreground border-white/15 bg-white/5" };
                  const hasTotals = a.action === "update" && (a.old_total != null || a.new_total != null);
                  return (
                    <div key={a.id} data-testid={`audit-row-${a.id}`} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${act.c}`}>{act.label}{a.action === "bulk_confirm" && a.count ? ` · ${a.count}` : ""}</span>
                        <span className="text-xs text-muted-foreground">{fmtAuditDate(a.at)}</span>
                      </div>
                      <div className="mt-1.5 text-sm">
                        <span className="text-foreground font-medium">{a.athlete_name || "—"}</span>
                        {a.apparatus ? <span className="text-muted-foreground capitalize"> · {a.apparatus}</span> : null}
                        {a.judge_name ? <span className="text-muted-foreground"> · voto di {a.judge_name}</span> : null}
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                        <span>da <span className="text-foreground/80">{a.actor_name}</span> ({a.actor_role})</span>
                        {a.action === "create" && a.new_total != null && <span className="text-gold">Tot. {Number(a.new_total).toFixed(3)}</span>}
                        {hasTotals && <span className="text-amber-300">{a.old_total != null ? Number(a.old_total).toFixed(3) : "—"} → {a.new_total != null ? Number(a.new_total).toFixed(3) : "—"}</span>}
                        {a.action === "delete" && a.old_total != null && <span className="text-red-300">era Tot. {Number(a.old_total).toFixed(3)}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter className="border-t border-white/10 pt-3 mt-1">
            <Button data-testid="audit-export-csv" variant="outline" onClick={exportAuditCSV} disabled={auditLoading || auditFiltered.length === 0} className="border-white/15">
              <FileDown className="w-4 h-4 mr-1.5" /> Esporta CSV
            </Button>
            <Button data-testid="audit-export-pdf" onClick={exportAuditPDF} disabled={auditLoading || auditFiltered.length === 0} className="bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
              <FileDown className="w-4 h-4 mr-1.5" /> Esporta PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog anti-errore */}
      <Dialog open={!!anomaly} onOpenChange={(v) => { if (!v) setAnomaly(null); }}>
        <DialogContent data-testid="anomaly-dialog" className="bg-[#0F172A] border-amber-500/40 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-300"><AlertTriangle className="w-5 h-5" /> Totale anomalo</DialogTitle>
            <DialogDescription className="sr-only">Avviso di conferma per un totale anomalo rispetto agli altri giudici</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{anomaly?.reason} Vuoi confermarlo comunque?</p>
          <DialogFooter className="mt-3">
            <Button data-testid="anomaly-cancel" variant="outline" onClick={() => setAnomaly(null)} className="border-white/15">Rivedi il voto</Button>
            <Button data-testid="anomaly-confirm" onClick={() => { const s = anomaly.score; setAnomaly(null); confirmScore(s); }} className="bg-amber-500 text-[#020617] hover:bg-amber-400 font-semibold">Conferma comunque</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert voti non confermati */}
      {pending.count > 0 && (
        <div data-testid="pending-scores-alert" className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-amber-300">{isJudge ? "Hai" : "Ci sono"} {pending.count} {pending.count === 1 ? "voto inserito ma non ancora confermato" : "voti inseriti ma non ancora confermati"}</p>
            <p className="text-sm text-muted-foreground mt-0.5">I voti non confermati non entrano nel calcolo. Conferma ogni voto con l'apposito tasto per registrarlo.</p>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {pending.items.slice(0, 8).map((p) => (
                <span key={p.id} className="text-xs px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-200">{p.athlete_name} · {p.apparatus || "—"}{!isJudge ? ` · ${p.judge_name}` : ""}</span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Form inserimento */}
        <div className="rounded-xl border border-white/10 bg-[#0F172A] p-6">
          <div className="flex items-center gap-2 mb-5">
            <Gavel className="w-5 h-5 text-gold" />
            <h3 className="text-lg font-semibold">{editingId ? "Modifica Voto" : "Inserimento Voto"}</h3>
          </div>
          <div className="space-y-4">
            {isJudge ? (
              pedana?.current ? (
                <div data-testid="judge-pedana-lock" className="rounded-lg border border-gold/40 bg-gold/10 px-4 py-3">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gold mb-1">
                    <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-60"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-gold"></span></span>
                    Atleta in pedana — stai valutando
                  </div>
                  <div className="font-display text-xl text-foreground">{pedana.current.name}</div>
                  <div className="text-sm text-muted-foreground">{pedana.current.sub}{pedana.current.discipline ? ` · ${discLabel[pedana.current.discipline] || pedana.current.discipline}` : ""}</div>
                </div>
              ) : (
                <div data-testid="judge-pedana-waiting" className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-4 flex items-center gap-3">
                  <MonitorPlay className="w-5 h-5 text-amber-400 shrink-0" />
                  <div>
                    <p className="font-medium text-amber-300">In attesa dell'atleta in pedana</p>
                    <p className="text-sm text-muted-foreground">Il Direttore/Segreteria deve impostare l'atleta corrente dalla sezione Programma. Comparirà qui automaticamente.</p>
                  </div>
                </div>
              )
            ) : (
              <div>
                <Label>Atleta</Label>
                <Select value={athleteId} onValueChange={selectAthlete}>
                  <SelectTrigger data-testid="score-athlete-select" className="mt-1.5"><SelectValue placeholder="Seleziona atleta" /></SelectTrigger>
                  <SelectContent>
                    {athletes.map((a) => <SelectItem key={a.id} value={a.id}>{a.first_name} {a.last_name} · {discLabel[a.discipline]} · {a.category}</SelectItem>)}
                    {groups.map((g) => <SelectItem key={g.id} value={g.id}>Gruppo: {g.name} · {discLabel[g.discipline]} · {g.category}</SelectItem>)}
                  </SelectContent>
                </Select>
                {pedana?.current && pedana.current.id !== athleteId && (
                  <button data-testid="manager-goto-pedana" onClick={() => selectAthlete(pedana.current.id)} className="mt-2 inline-flex items-center gap-1.5 text-xs text-gold hover:text-[#FDE047] transition-colors">
                    <Radio className="w-3.5 h-3.5" /> In pedana: <b>{pedana.current.name}</b> — vai a questo atleta
                  </button>
                )}
              </div>
            )}
            {isManager && (
              <div>
                <Label>Giudice {editingId ? "" : "(a cui attribuire il voto)"}</Label>
                <Select value={form.judge_id} onValueChange={(v) => setForm({ ...form, judge_id: v })} disabled={!!editingId}>
                  <SelectTrigger data-testid="score-judge-select" className="mt-1.5"><SelectValue placeholder="Seleziona giudice" /></SelectTrigger>
                  <SelectContent>
                    {judges.map((j) => <SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {selectedIsGroup ? (
              <div data-testid="group-score-note" className="rounded-lg border border-gold/25 bg-gold/5 px-4 py-3 text-sm text-muted-foreground">
                Punteggio complessivo di gruppo (senza attrezzo): inserisci il voto unico per <span className="text-gold font-medium">{selectedGroup.name}</span>.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo di esercizio</Label>
                  <Select value={form.exercise_type} onValueChange={(v) => setForm({ ...form, exercise_type: v })}>
                    <SelectTrigger data-testid="score-exercise-select" className="mt-1.5"><SelectValue placeholder="Seleziona tipo" /></SelectTrigger>
                    <SelectContent>{EXERCISE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Attrezzo</Label>
                  <Select value={form.apparatus} onValueChange={(v) => setForm({ ...form, apparatus: v })}>
                    <SelectTrigger data-testid="score-apparatus-select" className="mt-1.5"><SelectValue placeholder="Seleziona attrezzo" /></SelectTrigger>
                    <SelectContent>
                      {(selected?.apparatus?.length ? selected.apparatus : APPARATUS).map((a) => <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Difficoltà (D)</Label><Input data-testid="score-difficulty-input" type="number" step="0.1" value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Esecuzione (E)</Label><Input data-testid="score-execution-input" type="number" step="0.1" value={form.execution} onChange={(e) => setForm({ ...form, execution: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Artistico (A)</Label><Input data-testid="score-artistry-input" type="number" step="0.1" value={form.artistry} onChange={(e) => setForm({ ...form, artistry: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Penalità (−)</Label><Input data-testid="score-penalty-input" type="number" step="0.1" value={form.penalty} onChange={(e) => setForm({ ...form, penalty: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Note</Label><Input data-testid="score-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>

            <div className="flex items-center justify-between rounded-lg bg-gold/10 border border-gold/30 px-4 py-3">
              <span className="flex items-center gap-2 text-sm text-gold"><Calculator className="w-4 h-4" /> Totale voto</span>
              <span data-testid="score-live-total" className="font-display text-3xl text-gold">{liveTotal}</span>
            </div>
            <div className="flex gap-3">
              <Button data-testid="submit-score-btn" onClick={submit} disabled={saving || !athleteId} className="flex-1 bg-gold text-[#020617] hover:bg-[#FDE047] font-semibold">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editingId ? "Salva Modifica" : "Salva Voto (bozza)"}
              </Button>
              {editingId && <Button data-testid="cancel-edit-score-btn" onClick={cancelEdit} variant="outline" className="border-white/15">Annulla</Button>}
            </div>
            <p className="text-xs text-muted-foreground">Il voto viene salvato come <b>bozza</b>: va poi <b>confermato</b> con l'apposito tasto per essere registrato ed entrare nel calcolo.</p>
          </div>
        </div>

        {/* Voti registrati */}
        <div className="rounded-xl border border-white/10 bg-[#0F172A] p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">{selected ? `Voti · ${selectedName}` : "Voti registrati"}</h3>
            {athleteId && (
              <span data-testid="scores-live-indicator" className="inline-flex items-center gap-1.5 text-xs text-[#10B981] border border-[#10B981]/30 bg-[#10B981]/10 rounded-full px-2.5 py-1">
                <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-60"></span><span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]"></span></span>
                Live
              </span>
            )}
          </div>

          {!athleteId ? (
            <p className="text-muted-foreground text-sm py-10 text-center">Seleziona un atleta per vedere i voti.</p>
          ) : (
            <>
              {/* Riepilogo completamento per attrezzo */}
              {Object.keys(byApparatus).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4" data-testid="completion-summary">
                  {Object.entries(byApparatus).map(([app, g]) => {
                    const complete = activeJudges > 0 && g.confirmed.size >= activeJudges;
                    return (
                      <div key={app} data-testid={`completion-${app}`} className={`text-xs px-3 py-1.5 rounded-lg border flex items-center gap-1.5 ${complete ? "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]" : "border-white/15 bg-white/5 text-muted-foreground"}`}>
                        {complete ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                        <span className="capitalize font-medium">{app}</span>
                        <span>{g.confirmed.size}/{activeJudges} confermati</span>
                        {complete && <span className="font-display text-sm text-gold ml-1">= {g.total.toFixed(3)}</span>}
                        {isManager && g.pending > 0 && (
                          <button data-testid={`bulk-confirm-${app}`} onClick={() => bulkConfirm(app)} title="Conferma tutti i voti di questo attrezzo" className="ml-1.5 inline-flex items-center gap-1 text-[#10B981] hover:text-[#34D399] font-medium border-l border-white/10 pl-1.5">
                            <CheckCheck className="w-3.5 h-3.5" /> Conferma tutti ({g.pending})
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {scores.length === 0 ? (
                <p className="text-muted-foreground text-sm py-10 text-center">Nessun voto inserito.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead>Giudice</TableHead><TableHead>Attrezzo</TableHead>
                      <TableHead className="text-right">Tot.</TableHead><TableHead>Stato</TableHead><TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {scores.map((s) => (
                      <TableRow key={s.id} data-testid={`score-row-${s.id}`} className="border-white/10">
                        <TableCell className="font-medium">{s.judge_name}</TableCell>
                        <TableCell className="text-muted-foreground text-xs capitalize">{s.apparatus || "—"}</TableCell>
                        <TableCell className="text-right font-semibold text-gold">{Number(s.total).toFixed(3)}</TableCell>
                        <TableCell>
                          {s.confirmed
                            ? <span data-testid={`score-status-${s.id}`} className="text-xs inline-flex items-center gap-1 text-[#10B981]"><CheckCircle2 className="w-3.5 h-3.5" /> Confermato</span>
                            : <span data-testid={`score-status-${s.id}`} className="text-xs inline-flex items-center gap-1 text-amber-400"><Clock className="w-3.5 h-3.5" /> Bozza</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2.5">
                            {canManage(s) && !s.confirmed && (
                              <button data-testid={`confirm-score-${s.id}`} onClick={() => checkAndConfirm(s)} title="Conferma voto" className="text-[#10B981] hover:text-[#34D399] transition-colors"><Check className="w-4 h-4" /></button>
                            )}
                            {canManage(s) && s.confirmed && (
                              <button data-testid={`unconfirm-score-${s.id}`} onClick={() => unconfirmScore(s)} title="Annulla conferma" className="text-muted-foreground hover:text-amber-400 transition-colors"><Clock className="w-4 h-4" /></button>
                            )}
                            {canManage(s) && <button data-testid={`edit-score-${s.id}`} onClick={() => startEdit(s)} title="Modifica" className="text-muted-foreground hover:text-gold transition-colors"><Pencil className="w-4 h-4" /></button>}
                            {canManage(s) && <button data-testid={`delete-score-${s.id}`} onClick={() => remove(s.id)} title="Elimina" className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="w-4 h-4" /></button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
